import { ContentArea } from './ContentArea';
import { PageMode, ViewMode } from './Enums';
import { IReportController } from './Interfaces';
import { PageInfo } from './Models';
import { PageActionEventArgs } from './Models/PageActionEventArgs';
import { addClass, appendHtml, getElementAttributeValue, hasClass, prependHtml, removeClass } from './Utils/DomUtils';
import { debounce, stringFormat, throttle } from './Utils/Utils';

const skeletonTemplate: string =
    '<div class="trv-report-page trv-skeleton-page trv-skeleton-{0}" style="{1}" data-page="{0}"><div class="trv-skeleton-wrapper" style="{2}"></div></div>';

export class ScrollManager {
    private contentArea: ContentArea;
    private controller: IReportController;
    private enabled: boolean = false;
    private placeholder: HTMLElement;
    private pageContainer: HTMLElement;
    private pageWrapper: HTMLElement;
    private viewMode: ViewMode = ViewMode.Interactive;
    private scrollInProgress: boolean = false;
    private additionalTopOffset: number = 130;
    private onClickHandler: any = null;
    private debounceScroll: any = null;
    private throttleScroll: any = null;
    private oldScrollTopPosition: number = 0;
    private lastLoadedPage: PageInfo | null = null;

    constructor(placeholder: HTMLElement, contentArea: ContentArea, controller: IReportController) {
        this.placeholder = placeholder;
        this.pageContainer = placeholder.querySelector('.trv-page-container') as HTMLElement;
        this.pageWrapper = placeholder.querySelector('.trv-page-wrapper') as HTMLElement;

        this.contentArea = contentArea;
        this.controller = controller;

        if (this.controller.getPageMode() === PageMode.ContinuousScroll) {
            this.enable();
        }

        this.controller
            .on('loadedReportChange', this.disable.bind(this))
            .on('viewModeChanged', this.disable.bind(this))
            .on('scaleChanged', this.onScaleChanged.bind(this))
            .on('interactiveActionExecuting', this.onInteractiveActionExecuting.bind(this));
    }

    private onScaleChanged() {
        if (this.enabled) {
            setTimeout(() => {
                this.loadMorePages();
                this.keepCurrentPageInToView();
            }, 100);
        }
    }

    private onInteractiveActionExecuting(args: PageActionEventArgs) {
        const actionType = args.action.Type;

        if (this.enabled && (actionType === 'sorting' || actionType === 'toggleVisibility')) {
            this.disable();
        }
    }

    getEnabled(): boolean {
        return this.enabled;
    }

    enable() {
        this.enabled = true;
        addClass(this.placeholder, 'scrollable');
        this.initEvents();
    }

    disable() {
        if (this.enabled) {
            this.lastLoadedPage = null;
            this.pageWrapper.innerHTML = '';
            this.enabled = false;
            removeClass(this.placeholder, 'scrollable');
            this.unbind();
        }
    }

    renderPage(pageInfo: PageInfo) {
        let pageViewMode = this.controller.getViewMode();
        let renderedPage = this.findPageElement(pageInfo.pageNumber);

        if (!this.enabled) {
            this.enable();

            if (pageViewMode !== this.viewMode || !renderedPage) {
                this.updatePageArea(pageInfo);
            } else {
                this.render(pageInfo, true);
                this.pageContainer.scrollTop = 3;
                this.setCurrentPage(pageInfo.pageNumber);
            }

            this.viewMode = this.controller.getViewMode();
            this.loadMorePages();
        } else {
            if (pageViewMode !== this.viewMode || !renderedPage) {
                this.updatePageArea(pageInfo);
            } else {
                this.navigateToPage(pageInfo, renderedPage);
            }

            this.viewMode = this.controller.getViewMode();
        }
    }

    navigateToElement(offsetTop: number, pageNumber: number) {
        this.scrollInProgress = true;

        if (this.isSkeletonScreen(null, pageNumber)) {
            this.getAndRenderPage(pageNumber).then(() => {
                this.scrollToPage(offsetTop, pageNumber);
            });
        } else {
            this.scrollToPage(offsetTop, pageNumber);
        }
    }

    updatePageDimensionsReady() {
        if (this.enabled && this.currentPageNumber() > 0) {
            this.keepCurrentPageInToView();
        }
    }

    private setCurrentPage(pageNumber: number) {
        if (pageNumber !== this.currentPageNumber()) {
            this.controller.setCurrentPageNumber(pageNumber);
        }

        if (this.controller.getPageCount() > 1) {
            removeClass(this.findElement('.k-state-default'), 'k-state-default');
            addClass(this.findPageElement(pageNumber), 'k-state-default');
        }

        this.loadNextPreviousPage(pageNumber);
    }

    private updatePageArea(pageInfo: PageInfo) {
        let scrollTo = 0;
        let pageNumber = pageInfo.pageNumber;

        this.scrollInProgress = true;

        if (pageNumber > 1) {
            this.generateSkeletonScreens(pageNumber);
        }

        this.render(pageInfo, false);
        this.setCurrentPage(pageInfo.pageNumber);

        setTimeout(() => {
            scrollTo = pageNumber > 1 ? this.findPageElement(pageNumber)?.offsetTop || 0 : 0;

            this.animateScroll(scrollTo, 0, () => {
                this.scrollInProgress = false;
            });
        }, 100);
    }

    private navigateToPage(pageInfo: PageInfo, renderedPage: HTMLElement) {
        this.scrollInProgress = true;

        let scrollTo = renderedPage.offsetTop;
        let pageNumber = pageInfo.pageNumber;

        if (this.isSkeletonScreen(renderedPage, pageNumber)) {
            this.getAndRenderPage(pageNumber).then(() => {
                this.scrollToPage(scrollTo, pageNumber);
            });
        } else {
            this.updatePageContent(pageInfo, renderedPage);

            this.scrollToPage(scrollTo, pageNumber);
        }
    }

    private updatePageContent(pageInfo: PageInfo, renderedPage: HTMLElement) {
        this.contentArea.updatePageStyle(pageInfo);

        let page = this.contentArea.renderPageElement(pageInfo);
        renderedPage.after(page);
        renderedPage.remove();

        this.contentArea.setScrollablePage(page, pageInfo);

        this.controller.scrollPageReady();
    }

    private currentPageNumber(): number {
        return this.controller.getCurrentPageNumber();
    }

    private isSkeletonScreen(page: HTMLElement | null, pageNumber: number): boolean {
        if (!page) {
            // eslint-disable-next-line no-param-reassign
            page = this.findPageElement(pageNumber);

            if (!page) {
                return false;
            }
        }

        return hasClass(page, 'trv-skeleton-' + pageNumber);
    }

    private addSkeletonScreen(pageNumber: number, position: boolean) {
        let pageStyleNumber = pageNumber + (position ? 1 : -1);
        let pageStyleBaseDom = this.findPageElement(pageStyleNumber);
        let pageStyle = getElementAttributeValue(pageStyleBaseDom, 'style');
        let contentStyle = getElementAttributeValue(pageStyleBaseDom?.querySelector('sheet') as HTMLElement, 'style');
        let skeletonEl = stringFormat(skeletonTemplate, pageNumber, pageStyle, contentStyle);

        if (position) {
            prependHtml(this.pageWrapper, skeletonEl);
        } else {
            appendHtml(this.pageWrapper, skeletonEl);
        }
    }

    private generateSkeletonScreens(upToPageNumber: number) {
        let skeletonEl = '';
        let pageStyleBaseDom = this.findPageElement(1);
        let pageStyle = getElementAttributeValue(pageStyleBaseDom, 'style');
        let contentStyle = getElementAttributeValue(pageStyleBaseDom?.querySelector('sheet') as HTMLElement, 'style');
        let lastPage = this.findLastElement('.trv-report-page')?.dataset['page'];
        let index = lastPage ? parseInt(lastPage) + 1 : 1;

        for (index; index < upToPageNumber; index++) {
            skeletonEl = skeletonEl + stringFormat(skeletonTemplate, index, pageStyle, contentStyle);
        }

        appendHtml(this.pageWrapper, skeletonEl);
    }

    private loadMorePages() {
        let pageCount = this.controller.getPageCount();
        let isViewPortBiggerThanPageHeight = this.pageContainer.offsetHeight > this.pageWrapper.offsetHeight;

        if (pageCount > 1) {
            if (isViewPortBiggerThanPageHeight) {
                this.scrollInProgress = true;

                let lastPage = this.findLastElement('.trv-report-page')?.dataset['page'];
                let nextPage = lastPage ? parseInt(lastPage) + 1 : 1;

                if (nextPage <= pageCount) {
                    this.getAndRenderPage(nextPage).then(() => {
                        this.loadMorePages();
                        this.scrollInProgress = false;
                    });
                }
            } else {
                this.loadVisiblePages();
                this.scrollInProgress = false;
            }
        }
    }

    private loadVisiblePages() {
        this.placeholder.querySelectorAll('.trv-report-page').forEach((page) => {
            let pageItem = page as HTMLElement;
            let pageNumber = parseInt(pageItem.dataset['page'] || '');

            if (this.scrolledInToView(pageItem) && this.isSkeletonScreen(pageItem, pageNumber)) {
                this.getAndRenderPage(pageNumber);
            }
        });
    }

    private scrolledInToView(element: HTMLElement | null): boolean {
        if (!element) {
            return false;
        }

        let pageCoords = element.getBoundingClientRect();
        let pageParent = element.closest('.trv-pages-pane') as HTMLElement;

        if (!pageParent) {
            return false;
        }

        let parentCoords = pageParent.getBoundingClientRect();
        let parentTop = parentCoords.top;
        let parentBottom = parentCoords.bottom;
        let pageTop = pageCoords.top;
        let pageBottom = pageCoords.bottom;

        let additionalTopOffset = this.additionalTopOffset + parentTop;
        let topVisible = pageTop > 0 && pageTop < parentBottom;
        let bottomVisible = pageBottom < parentBottom && pageBottom > additionalTopOffset;

        return topVisible || bottomVisible;
    }

    private render(pageInfo: PageInfo, empty: boolean) {
        let pageNumber = pageInfo.pageNumber;
        let pageItem = this.findPageElement(pageNumber);

        if (!empty && pageItem && !this.isSkeletonScreen(pageItem, pageNumber)) {
            return;
        }

        if (this.lastLoadedPage == null || this.lastLoadedPage.pageNumber < pageInfo.pageNumber) {
            this.lastLoadedPage = pageInfo;
        }
        this.contentArea.updatePageStyle(this.lastLoadedPage);

        let page = this.contentArea.renderPageElement(pageInfo);

        if (empty) {
            this.pageWrapper.innerHTML = '';
        }

        this.pageWrapper.dataset['pageNumber'] = pageNumber.toString();

        let skeletonPage = this.findElement('.trv-skeleton-' + pageNumber);
        if (skeletonPage) {
            skeletonPage.after(page);
            skeletonPage.remove();
        } else {
            this.pageWrapper.append(page);
        }

        this.contentArea.setScrollablePage(page, pageInfo);

        this.controller.scrollPageReady();
    }

    private loadNextPreviousPage(pageNumber: number) {
        if (pageNumber < this.controller.getPageCount()) {
            let nextPage = pageNumber + 1;
            let nextItem = this.findPageElement(nextPage);

            if (nextItem && this.isSkeletonScreen(nextItem, nextPage)) {
                this.getAndRenderPage(nextPage);
            }
        }

        if (pageNumber > 1) {
            let previousPage = pageNumber - 1;
            let previousItem = this.findPageElement(previousPage);

            if (previousItem && this.isSkeletonScreen(previousItem, previousPage)) {
                // TODO Improve the condition above or the following commented code because currently, we are always loading the previous page instead of the desired one.
                // this.getAndRenderPage(previousPage);
            }
        }
    }

    private initEvents() {
        this.onClickHandler = this.clickPage.bind(this);

        this.debounceScroll = debounce(() => {
            let pages = this.placeholder.querySelectorAll('.trv-report-page');
            let scrollPosition = Math.round(this.pageContainer.scrollTop + this.pageContainer.offsetHeight);

            if (!this.scrollInProgress && pages.length && this.oldScrollTopPosition !== scrollPosition) {
                this.advanceCurrentPage(Array.from(pages));
            }
        }, 250);
        this.throttleScroll = throttle(() => {
            let pages = this.placeholder.querySelectorAll('.trv-report-page');
            let scrollPosition = Math.round(this.pageContainer.scrollTop + this.pageContainer.offsetHeight);

            if (!this.scrollInProgress && this.oldScrollTopPosition !== scrollPosition) {
                if (this.oldScrollTopPosition > scrollPosition) {
                    this.scrollUp(Array.from(pages));
                } else {
                    this.scrollDown(Array.from(pages), scrollPosition);
                }
            }

            this.oldScrollTopPosition = scrollPosition;
        }, 250);

        this.pageContainer.addEventListener('click', this.onClickHandler);
        this.pageContainer.addEventListener('scroll', this.debounceScroll);
        this.pageContainer.addEventListener('scroll', this.throttleScroll);
    }

    private unbind() {
        this.pageContainer.removeEventListener('click', this.onClickHandler);
        this.pageContainer.removeEventListener('scroll', this.debounceScroll);
        this.pageContainer.removeEventListener('scroll', this.throttleScroll);
    }

    private clickPage(args: any) {
        let page = args.target?.closest('.trv-report-page') as HTMLElement;
        if (!page) {
            return;
        }

        let currentPage = this.currentPageNumber();
        let pageNumber = parseInt(page.dataset['page'] || '');

        if (currentPage !== pageNumber) {
            if (this.isSkeletonScreen(page, pageNumber)) {
                this.getAndRenderPage(pageNumber).then((pageInfo: PageInfo) => {
                    this.setCurrentPage(pageInfo.pageNumber);
                });
            } else {
                this.setCurrentPage(pageNumber);
            }
        }
    }

    private advanceCurrentPage(pages: Element[]) {
        let newCurrentPage = this.findNewCurrentPage(pages);

        if (newCurrentPage) {
            let pageNumber = parseInt(newCurrentPage.dataset['page'] || '-1');
            let currentPageNumber = this.currentPageNumber();
            let currentPageIsInToView = this.scrolledInToView(this.findPageElement(currentPageNumber));

            if (currentPageNumber !== pageNumber && !currentPageIsInToView) {
                if (this.isSkeletonScreen(newCurrentPage, pageNumber)) {
                    this.getAndRenderPage(pageNumber).then((pageInfo: PageInfo) => {
                        this.setCurrentPage(pageInfo.pageNumber);
                    });
                } else {
                    this.setCurrentPage(pageNumber);
                }
            }
        } else {
            // eslint-disable-next-line no-console
            console.log('Page not found - ', newCurrentPage);
        }
    }

    // Binary search
    private findNewCurrentPage(pages: Element[]): HTMLElement | null {
        let middleIndex = Math.floor(pages.length / 2);
        let result = this.findPageInViewPort(middleIndex, pages);

        if (pages.length === 1) {
            return pages[0] as HTMLElement;
        }

        if (result === 0) {
            return pages[middleIndex] as HTMLElement;
        } else if (result < 0 && pages.length > 1) {
            return this.findNewCurrentPage(pages.splice(middleIndex, Number.MAX_VALUE));
        } else if (result > 0 && pages.length > 1) {
            return this.findNewCurrentPage(pages.splice(0, middleIndex));
        }

        return null;
    }

    private findPageInViewPort(index: number, pages: Element[]): number {
        let element = pages[index] as HTMLElement;
        let pageCoords = element.getBoundingClientRect();
        let pageParent = element.closest('.trv-pages-pane') as HTMLElement;

        if (!pageParent) {
            return -1;
        }
        let parentCoords = pageParent.getBoundingClientRect();
        let parentTop = parentCoords.top;
        let pageTop = pageCoords.top;
        let pageBottom = pageCoords.bottom;
        let additionalTopOffset = this.additionalTopOffset + parentTop;

        let isCurentPage = pageTop <= additionalTopOffset && additionalTopOffset < pageBottom;

        if (isCurentPage) {
            return 0;
        }

        if (pageBottom < additionalTopOffset) {
            return -1;
        }

        return 1;
    }

    private scrollDown(pages: Element[], scrollPosition: number) {
        if (scrollPosition >= this.pageContainer.scrollHeight) {
            let lastPage = parseInt((pages[pages.length - 1] as HTMLElement).dataset['page'] || '');
            let nextPage = lastPage + 1;

            if (this.currentPageNumber() < nextPage && nextPage <= this.controller.getPageCount()) {
                this.addSkeletonScreen(nextPage, false);

                this.getAndRenderPage(nextPage);
            }
        } else {
            this.advanceCurrentPage(pages);
            this.loadVisiblePages();
        }
    }

    private scrollUp(pages: Element[]) {
        if (this.pageContainer.scrollTop === 0) {
            let pageNumber = parseInt((pages[0] as HTMLElement).dataset['page'] || '');
            let previousPage = pageNumber - 1;

            if (this.currentPageNumber() > previousPage && previousPage >= 1) {
                this.addSkeletonScreen(previousPage, true);

                this.getAndRenderPage(previousPage).then(() => {
                    this.pageContainer.scrollTop = 3;
                });
            }
        } else {
            this.advanceCurrentPage(pages);
            this.loadVisiblePages();
        }
    }

    private keepCurrentPageInToView() {
        let currentPage = this.findPageElement(this.currentPageNumber());

        if (!currentPage) {
            return;
        }

        let currentPageTop = currentPage.offsetTop;
        let currentPageHeight = currentPage.offsetHeight;
        let pageContainerHeight = this.pageContainer.offsetHeight;

        this.scrollInProgress = true;

        if (currentPageHeight < pageContainerHeight) {
            let emptyView = (pageContainerHeight - currentPageHeight) / 2;

            currentPageTop = currentPageTop - emptyView;
        }

        this.animateScroll(currentPageTop, 0, () => {
            setTimeout(() => {
                this.scrollInProgress = false;
            }, 100);
        });
    }

    private getAndRenderPage(page: number): Promise<PageInfo> {
        return this.controller.getPageData(page).then((pageInfo: PageInfo) => {
            this.render(pageInfo, false);

            return pageInfo;
        });
    }

    private scrollToPage(scrollTo: number, pageNumber: number) {
        this.animateScroll(scrollTo, 500, () => {
            this.setCurrentPage(pageNumber);
            setTimeout(() => {
                this.scrollInProgress = false;
            });
        });
    }

    private animateScroll(scrollTop: number, duration: number, callback: any) {
        //this.pageContainer.animate({ scrollTop: scrollTop }, 0, callback);
        this.pageContainer.scrollTop = scrollTop;

        callback.call(this);
    }

    private findPageElement(pageNumber: number): HTMLElement | null {
        return this.findElement('[data-page="' + pageNumber + '"]');
    }

    private findElement(selector: string): HTMLElement | null {
        return this.placeholder?.querySelector(selector) as HTMLElement;
    }

    private findLastElement(selector: string): HTMLElement | null {
        let elements = this.placeholder?.querySelectorAll(selector);

        if (elements && elements.length) {
            return elements[elements.length - 1] as HTMLElement;
        }

        return null;
    }
}
